import type { ReactNode } from "react";
import type { FractionProblem } from "@/lib/tool-kit/worksheets/fractions";

const SYMBOL: Record<string, string> = {
  add: "+",
  subtract: "−",
  multiply: "×",
  divide: "÷",
};

/** A stacked fraction: numerator over a bar over the denominator. */
export function Frac({ n, d }: { n: ReactNode; d: ReactNode }) {
  return (
    <span className="inline-flex min-w-[1.6ch] flex-col items-stretch text-center font-mono tabular-nums leading-[1.15]">
      <span className="px-1">{n}</span>
      <span className="border-neutral-900 border-t px-1">{d}</span>
    </span>
  );
}

function BlankBox() {
  return (
    <span aria-hidden="true" className="inline-block h-[1.15em] min-w-[2.2ch]">
      &nbsp;
    </span>
  );
}

function BlankFraction() {
  return <Frac d={<BlankBox />} n={<BlankBox />} />;
}

function Circle() {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-[1.3em] w-[1.3em] rounded-full border border-neutral-900"
    />
  );
}

function Equals() {
  return <span className="font-mono">=</span>;
}

/** One printed fraction problem, laid out on a single line. */
export function FractionProblemView({ problem }: { problem: FractionProblem }) {
  const a = <Frac d={problem.a.d} n={problem.a.n} />;
  const b = problem.b ? <Frac d={problem.b.d} n={problem.b.n} /> : null;
  let body: ReactNode;
  if (problem.kind === "compare") {
    body = (
      <>
        {a}
        <Circle />
        {b}
      </>
    );
  } else if (problem.kind === "simplify") {
    body = (
      <>
        {a}
        <Equals />
        <BlankFraction />
      </>
    );
  } else if (problem.kind === "equivalent") {
    body = (
      <>
        {a}
        <Equals />
        <Frac d={problem.answer.d} n={<BlankBox />} />
      </>
    );
  } else {
    body = (
      <>
        {a}
        <span className="font-mono">{SYMBOL[problem.kind]}</span>
        {b}
        <Equals />
        <BlankFraction />
      </>
    );
  }
  return (
    <div className="inline-flex items-center gap-2 whitespace-nowrap py-1">
      {body}
    </div>
  );
}
